'use client';

import { useMemo } from 'react';
import { cn } from '@/shared/utils/tw';
import { useToastStore } from '@/layouts/custom-avatar-page/toast/model/toast-store';
import { KioskToast } from './kiosk-toast';

/**
 * 키오스크 전용 토스트 컨테이너
 *
 * Figma 디자인 기준:
 * - 위치: y=2828 in 3840 height → bottom ~26.4%
 * - 중앙 정렬
 * - STT/TTS 토스트만 Figma Bubble 스타일로 렌더링
 * - 기타 토스트(text, info, warning, error)는 기존 스타일 유지
 */
export const KioskToastContainer = () => {
  const toasts = useToastStore((state) => state.toasts);

  // STT/TTS 토스트만 필터링 (키오스크에서는 이 타입만 Bubble 스타일 적용)
  const bubbleToasts = useMemo(
    () => toasts.filter((t) => t.type === 'stt' || t.type === 'tts'),
    [toasts]
  );

  // 기타 토스트 (text, info, warning, error) - 기존 스타일 유지
  const otherToasts = useMemo(
    () => toasts.filter((t) => t.type !== 'stt' && t.type !== 'tts'),
    [toasts]
  );

  if (toasts.length === 0) return null;

  return (
    <>
      {/* 키오스크 Bubble 스타일 토스트 (STT/TTS) */}
      {bubbleToasts.length > 0 && (
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2 z-[200]',
            // Figma: 버블 y=2750, h=216 → bottom=(3840-2966)/3840=22.8%
            'bottom-[22.8%]',
            // 너비 지정 (토스트가 80% 너비를 가질 수 있도록)
            'w-[80%]',
            'flex flex-col items-center gap-4',
            'pointer-events-none'
          )}
        >
          {bubbleToasts.map((toast) => (
            <KioskToast key={toast.id} toast={toast} />
          ))}
        </div>
      )}

      {/* 기타 토스트 - 키오스크에서는 버블 토스트 위에 표시 */}
      {otherToasts.length > 0 && (
        <div
          className={cn(
            'absolute left-1/2 -translate-x-1/2 z-[200]',
            // 키오스크: 버블 토스트 위쪽에 배치 (bottom-[28%])
            'bottom-[28%]',
            'w-[80%]',
            'flex flex-col items-center gap-2',
            'pointer-events-none'
          )}
        >
          {otherToasts.map((toast) => (
            <div
              key={toast.id}
              className={cn(
                'bg-[rgba(50,52,56,0.5)]',
                'backdrop-blur-[20px]',
                '[-webkit-backdrop-filter:blur(20px)]',
                'border-2 border-[rgba(255,255,255,0.08)]',
                'rounded-[20px]',
                'px-[18px] py-3',
                'font-pretendard text-sm font-normal text-white',
                'pointer-events-auto',
                'max-w-[min(85cqw,400px)]',
                toast.isExiting ? 'animate-toast-exit' : 'animate-toast-enter'
              )}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default KioskToastContainer;
